import { useEffect, useState } from "react";

interface Progress {
  upload_id: string;
  total: number;
  ocr_complete: number;
  ocr_running: number;
  ocr_pending: number;
  pending_review: number;
  other: number;
  jobs_failed: number;
  jobs_retry: number;
  /** Fractional credit (0..0.99 per running card) so progress moves smoothly
   *  while OCR is in flight, not just at completion boundaries. */
  partial_progress: number;
  done: boolean;
}

interface Props {
  uploadId: string;
  filename: string;
  onDone: () => void;
  onDismiss: () => void;
}

export function UploadProgressBanner({ uploadId, filename, onDone, onDismiss }: Props) {
  const [progress, setProgress] = useState<Progress | null>(null);
  const [connErr, setConnErr] = useState(false);

  useEffect(() => {
    const url = new URL(`api/uploads/${encodeURIComponent(uploadId)}/events`, document.baseURI).toString();
    const es = new EventSource(url, { withCredentials: true });
    es.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data) as Progress;
        setProgress(data);
        setConnErr(false);
        if (data.done) {
          es.close();
          setTimeout(onDone, 2500);
        }
      } catch { /* malformed event */ }
    };
    es.onerror = () => setConnErr(true);
    return () => es.close();
  }, [uploadId, onDone]);

  if (!progress) {
    return (
      <div className="upload-progress-banner">
        <div className="upload-progress-row">
          <span>⏳ <strong>{filename}</strong> — verbinde …</span>
          <button className="link" onClick={onDismiss}>ausblenden</button>
        </div>
      </div>
    );
  }

  const done = progress.ocr_complete + progress.pending_review + progress.other;
  // Blended progress: completed cards (full credit) + in-flight cards
  // (fractional credit by elapsed/budget). Avoids the 0% → 100% jump when
  // a single in-flight card finishes.
  const partial = progress.partial_progress ?? 0;
  const blended = progress.total > 0
    ? Math.min(99.9, ((done + partial) / progress.total) * 100)
    : 0;
  // Display: round blended pct down to integer; only show 100% when actually done.
  const pct = progress.done ? 100 : Math.floor(blended);
  // Bar segments: solid for completed, animated for in-flight contribution.
  const completedPct = progress.total > 0 ? (done / progress.total) * 100 : 0;
  const runningPct = Math.max(0, blended - completedPct);

  const parts: string[] = [];
  if (progress.ocr_complete > 0) parts.push(`✅ ${progress.ocr_complete} OCR fertig`);
  if (progress.pending_review > 0) parts.push(`❓ ${progress.pending_review} unklar`);
  if (progress.ocr_running > 0) parts.push(`🔄 ${progress.ocr_running} läuft`);
  if (progress.ocr_pending > 0) parts.push(`⏳ ${progress.ocr_pending} wartet`);
  if (progress.jobs_retry > 0) parts.push(`↻ ${progress.jobs_retry} retry`);

  return (
    <div className={`upload-progress-banner${progress.done ? " done" : ""}`}>
      <div className="upload-progress-row">
        <strong>📄 {filename}</strong>
        <span className="upload-progress-pct">{pct}% · {done}/{progress.total}</span>
        <button className="link" onClick={onDismiss}>ausblenden</button>
      </div>
      <div className="upload-progress-bar">
        <div
          className="upload-progress-bar-fill"
          style={{ width: `${completedPct}%` }}
        />
        {runningPct > 0 && !progress.done && (
          <div
            className="upload-progress-bar-running"
            style={{ left: `${completedPct}%`, width: `${runningPct}%` }}
            title={`${progress.ocr_running} laufende Karte${progress.ocr_running === 1 ? "" : "n"}`}
          />
        )}
      </div>
      <div className="upload-progress-detail">
        {parts.map((p, i) => <span key={i}>{p}</span>)}
        {progress.jobs_failed > 0 && (
          <span className="error">✗ {progress.jobs_failed} im DLQ</span>
        )}
        {connErr && <span className="error">Verbindung unterbrochen — lade Seite neu</span>}
      </div>
    </div>
  );
}
